import { clientSDK, clientV3SDK } from "@istari-frontend/api-client";
import { V3Models } from "@istari/istari-client";
import { infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";

import { DEFAULT_CACHE_TIME } from "~/constants";
import { ConnectionKind } from "~/secure-connections/model/secure-connection";

import { withErrorDetail } from "../adapter/registry-request-error";

// Common shape of the sending and receiving update results, so the kind-branched
// dry run resolves to one type.
type ConnectionUpdateResult = { outcomes: V3Models.ConnectionUpdateOutcomesDto };

const LIST_PAGE_SIZE = 100;
const SYNC_STATUS_PAGE_SIZE = 10;

const SYNC_STATUS_SDK_BY_KIND = {
  receiving: clientV3SDK.listReceivingRemoteSyncStatuses,
  sending: clientV3SDK.listSendingRemoteSyncStatuses,
} as const;

export const secureConnectionQueryKeys = {
  all: () => ["secure-connections"],
  receiving: () => [...secureConnectionQueryKeys.all(), "receiving"],
  receivingList: (showArchived: boolean) => [
    ...secureConnectionQueryKeys.receiving(),
    "list",
    "infinite",
    { showArchived },
  ],
  receivingDetail: (id: string) => [...secureConnectionQueryKeys.receiving(), "detail", id],
  receivingOwners: (id: string) => [...secureConnectionQueryKeys.receiving(), "owners", id],
  receivingIncomingControlTags: (id: string) => [...secureConnectionQueryKeys.receiving(), "incoming-control-tags", id],
  receivingInfosecLevels: (id: string) => [...secureConnectionQueryKeys.receiving(), "infosec-levels", id],
  receivingInfosecLevelMappings: (id: string) => [
    ...secureConnectionQueryKeys.receiving(),
    "infosec-level-mappings",
    id,
  ],
  archiveImpact: (kind: ConnectionKind, id: string) => [...secureConnectionQueryKeys[kind](), "archive-impact", id],
  syncStatuses: (kind: ConnectionKind, id: string) => [...secureConnectionQueryKeys[kind](), "sync-statuses", id],
  syncStatusesWithUpdates: (kind: ConnectionKind, id: string) => [
    ...secureConnectionQueryKeys[kind](),
    "sync-statuses",
    id,
    "with-updates",
  ],
  syncStatusLatest: (kind: ConnectionKind, id: string) => [
    ...secureConnectionQueryKeys[kind](),
    "sync-statuses",
    id,
    "latest",
  ],
  sending: () => [...secureConnectionQueryKeys.all(), "sending"],
  sendingList: (showArchived: boolean) => [
    ...secureConnectionQueryKeys.sending(),
    "list",
    "infinite",
    { showArchived },
  ],
  shareableSendingList: () => [...secureConnectionQueryKeys.sending(), "shareable", "list", "infinite"],
  sendingDetail: (id: string) => [...secureConnectionQueryKeys.sending(), "detail", id],
  sendingPermittedSenders: (id: string) => [...secureConnectionQueryKeys.sending(), "permitted-senders", id],
  objectStores: () => [...secureConnectionQueryKeys.all(), "object-stores", "infinite"],
  tenants: () => [...secureConnectionQueryKeys.all(), "tenants", "infinite"],
  shares: () => [...secureConnectionQueryKeys.all(), "shares"],
  sharesForResource: (resourceId: string) => [...secureConnectionQueryKeys.shares(), resourceId, "infinite"],
  resourceSyncStatusByConnection: (resourceId: string) => [
    ...secureConnectionQueryKeys.all(),
    "resource-sync-status",
    resourceId,
  ],
};

export const secureConnectionQueryOptions = {
  // Archive dry run: same validations and impact computation, no write. gcTime 0
  // because the count tracks live share state and goes stale as shares change.
  archiveImpact: (kind: ConnectionKind, id: string) =>
    queryOptions({
      queryKey: secureConnectionQueryKeys.archiveImpact(kind, id),
      queryFn: (): Promise<ConnectionUpdateResult> =>
        kind === "sending"
          ? clientV3SDK.updateSendingRemote({
              remoteId: id,
              updateSendingConnectionDto: { isArchived: true },
              dryRun: true,
            })
          : clientV3SDK.updateReceivingRemote({
              remoteId: id,
              updateReceivingConnectionDto: { isArchived: true },
              dryRun: true,
            }),
      gcTime: 0,
      enabled: !!id,
    }),
  receivingListInfinite: (showArchived: boolean) =>
    infiniteQueryOptions({
      queryKey: secureConnectionQueryKeys.receivingList(showArchived),
      queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
        withErrorDetail(() =>
          clientV3SDK.listReceivingRemotes({
            cursor: pageParam ?? null,
            size: LIST_PAGE_SIZE,
            showArchived,
          }),
        ),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
      placeholderData: keepPreviousData,
    }),
  sendingListInfinite: (showArchived: boolean) =>
    infiniteQueryOptions({
      queryKey: secureConnectionQueryKeys.sendingList(showArchived),
      queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
        withErrorDetail(() =>
          clientV3SDK.listSendingRemotes({
            cursor: pageParam ?? null,
            size: LIST_PAGE_SIZE,
            showArchived,
          }),
        ),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
      placeholderData: keepPreviousData,
    }),
  // Non-admin listing of the sending connections a file may be shared to. Unlike
  // sendingListInfinite (admin-only), any authenticated user may call this, so it
  // backs the share dialog and file-details sync views for non-admins. Returns a
  // slim projection without connection configuration.
  shareableSendingListInfinite: () =>
    infiniteQueryOptions({
      queryKey: secureConnectionQueryKeys.shareableSendingList(),
      queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
        clientV3SDK.listShareableSendingConnections({
          cursor: pageParam ?? null,
          size: LIST_PAGE_SIZE,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
      placeholderData: keepPreviousData,
    }),
  receivingById: (id: string) =>
    queryOptions({
      queryKey: secureConnectionQueryKeys.receivingDetail(id),
      queryFn: () => clientV3SDK.getReceivingRemote({ remoteId: id }),
      enabled: !!id,
    }),
  receivingOwners: (id: string) =>
    queryOptions({
      queryKey: secureConnectionQueryKeys.receivingOwners(id),
      queryFn: () => clientV3SDK.getReceivingConnectionOwners({ remoteId: id }),
      enabled: !!id,
    }),
  receivingIncomingControlTags: (id: string) =>
    queryOptions({
      queryKey: secureConnectionQueryKeys.receivingIncomingControlTags(id),
      queryFn: () => clientV3SDK.getReceivingConnectionControlTags({ remoteId: id }),
      enabled: !!id,
    }),
  receivingInfosecLevels: (id: string) =>
    queryOptions({
      queryKey: secureConnectionQueryKeys.receivingInfosecLevels(id),
      queryFn: () => clientV3SDK.getReceivingConnectionInfosecLevels({ remoteId: id }),
      enabled: !!id,
    }),
  receivingInfosecLevelMappings: (id: string) =>
    queryOptions({
      queryKey: secureConnectionQueryKeys.receivingInfosecLevelMappings(id),
      queryFn: () => clientV3SDK.getReceivingConnectionInfosecLevelMappings({ remoteId: id }),
      enabled: !!id,
    }),
  syncStatusesInfinite: (kind: ConnectionKind, id: string) =>
    infiniteQueryOptions({
      queryKey: secureConnectionQueryKeys.syncStatuses(kind, id),
      queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
        SYNC_STATUS_SDK_BY_KIND[kind]({
          remoteId: id,
          cursor: pageParam ?? null,
          size: SYNC_STATUS_PAGE_SIZE,
          includeTotal: true,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
      enabled: !!id,
      placeholderData: keepPreviousData,
    }),
  // onlyWithUpdates filters to sync events carrying resource updates server-side.
  syncStatusesWithUpdatesInfinite: (kind: ConnectionKind, id: string) =>
    infiniteQueryOptions({
      queryKey: secureConnectionQueryKeys.syncStatusesWithUpdates(kind, id),
      queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
        SYNC_STATUS_SDK_BY_KIND[kind]({
          remoteId: id,
          onlyWithUpdates: true,
          cursor: pageParam ?? null,
          size: SYNC_STATUS_PAGE_SIZE,
          includeTotal: true,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
      enabled: !!id,
      placeholderData: keepPreviousData,
    }),
  syncStatusLatest: (kind: ConnectionKind, id: string) =>
    queryOptions({
      queryKey: secureConnectionQueryKeys.syncStatusLatest(kind, id),
      queryFn: () => SYNC_STATUS_SDK_BY_KIND[kind]({ remoteId: id, cursor: null, size: 1 }),
      enabled: !!id,
    }),
  sendingById: (id: string) =>
    queryOptions({
      queryKey: secureConnectionQueryKeys.sendingDetail(id),
      queryFn: () => clientV3SDK.getSendingRemote({ remoteId: id }),
      enabled: !!id,
    }),
  sendingPermittedSenders: (id: string) =>
    queryOptions({
      queryKey: secureConnectionQueryKeys.sendingPermittedSenders(id),
      queryFn: () => clientV3SDK.getSendingConnectionPermittedSenders({ remoteId: id }),
      enabled: !!id,
    }),
  objectStoresInfinite: () =>
    infiniteQueryOptions({
      queryKey: secureConnectionQueryKeys.objectStores(),
      queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
        withErrorDetail(() => clientV3SDK.listObjectStores({ cursor: pageParam ?? null, size: LIST_PAGE_SIZE })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
    }),
  // Registry returns only tenants the caller administers.
  tenantsInfinite: () =>
    infiniteQueryOptions({
      queryKey: secureConnectionQueryKeys.tenants(),
      queryFn: ({ pageParam }: { pageParam: number }) =>
        withErrorDetail(() => clientSDK.listTenants(pageParam, LIST_PAGE_SIZE)),
      initialPageParam: 1,
      getNextPageParam: (lastPage) => (lastPage.page < lastPage.pages ? lastPage.page + 1 : undefined),
      staleTime: DEFAULT_CACHE_TIME,
    }),
  // Returns the most recent sync status the resource has on each secure connection
  // (one row per remote that has reported on the resource). Backed by the
  // per-resource sync-status endpoint, so this is bounded by the number of
  // connections the resource has been shared to — typically very small.
  resourceSyncStatusByConnection: (resourceId: string | undefined) =>
    queryOptions({
      queryKey: secureConnectionQueryKeys.resourceSyncStatusByConnection(resourceId ?? ""),
      queryFn: () => {
        if (!resourceId) throw new Error("resourceSyncStatusByConnection queried without resourceId");
        return clientV3SDK.listResourceSyncStatusByConnection({ resourceId });
      },
      enabled: !!resourceId,
    }),
  // Shares list excludes archived (revoked) shares by default — the app never
  // surfaces them, so we don't expose a toggle.
  sharesForResourceInfinite: (resourceId: string | undefined) =>
    infiniteQueryOptions({
      queryKey: secureConnectionQueryKeys.sharesForResource(resourceId ?? ""),
      queryFn: ({ pageParam }: { pageParam: string | undefined }) => {
        if (!resourceId) throw new Error("sharesForResourceInfinite queried without resourceId");
        return clientV3SDK.listSendingRemoteSharesForResource({
          resourceId,
          cursor: pageParam ?? null,
          size: LIST_PAGE_SIZE,
          showArchived: false,
        });
      },
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
      enabled: !!resourceId,
      placeholderData: keepPreviousData,
    }),
};
